import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import Loading from "../components/Loading";

function MapController({ driverLocations }) {
    const map = useMap();
    const hasFitted = useRef(false);

    useEffect(() => {
        if (!map || hasFitted.current) return;
        const google = window.google;
        if (!google?.maps) return;

        const bounds = new google.maps.LatLngBounds();
        let hasPoints = false;

        if (driverLocations) {
            Object.values(driverLocations).forEach((loc) => {
                bounds.extend({ lat: Number(loc.latitude), lng: Number(loc.longitude) });
                hasPoints = true;
            });
        }

        if (hasPoints) {
            map.fitBounds(bounds, 40);
            hasFitted.current = true;
        }
    }, [map, driverLocations]);

    return null;
}

export default function RoutesPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [date, setDate] = useState(location.state?.date || moment().format("YYYY-MM-DD"));
    const [routes, setRoutes] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [driverLocations, setDriverLocations] = useState({});
    const dateInputRef = useRef(null);

    const defaultCenter = { lat: 48.8566, lng: 2.3522 };

    const fetchDriverLocations = useCallback(async (routeIds, lastLocation = true) => {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/v2/routes/drivers/locations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ routeIds, lastLocation }),
            });
            if (response.ok) {
                const data = await response.json();
                setDriverLocations(data);
            }
        } catch (err) {
            console.error("Erreur lors du chargement des positions livreurs", err);
        }
    }, []);

    const fetchRoutes = useCallback(async (background = false) => {
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/v2/routes/overview?date=${date}`);
            if (!response.ok) {
                const err = await response.json().catch(() => null);
                setError(err?.error || "Aucune route disponible");
                return;
            }
            const data = await response.json();
            setRoutes(data.data);
            const inProgressIds = data.data
                .filter((r) => r.route.delivery_status === "in_progress")
                .map((r) => r.route.id);
            if (inProgressIds.length > 0) {
                fetchDriverLocations(inProgressIds);
            } else {
                setDriverLocations({});
            }
        } catch (err) {
            if (!background) {
                setError("Erreur lors du chargement des routes.");
            }
        } finally {
            if (!background) {
                setLoading(false);
            }
        }
    }, [date, fetchDriverLocations]);

    useEffect(() => {
        fetchRoutes();
        const interval = setInterval(() => fetchRoutes(true), 15000);
        return () => clearInterval(interval);
    }, [fetchRoutes]);

    function handleRouteClick(route) {
        navigate(`/routes/${route.route.id}`, { state: { date } });
    }

    return (
        <div className="card mb-4">
            <div className="card-header py-3">
                <div className="row py-3">
                    <div className="col-sm-6 text-center text-sm-start">
                        <h5 className="mb-4 mb-sm-0 mt-2">
                            Opération
                        </h5>
                    </div>
                    <div className="col-sm-6 text-center text-sm-end">
                        <div className="btn-group" role="group">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            data-mdb-ripple-init
                            onClick={() => setDate(moment(date).subtract(1, "day").format("YYYY-MM-DD"))}
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            data-mdb-ripple-init
                            onClick={() => dateInputRef.current?.showPicker()}
                        >
                            {moment(date).format("ddd DD MMM")}
                            <input
                                ref={dateInputRef}
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
                            />
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            data-mdb-ripple-init
                            onClick={() => setDate(moment(date).add(1, "day").format("YYYY-MM-DD"))}
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    </div>
                </div>
            </div>
            <div className="card-body">
                {loading ? (
                    <Loading />
                ) : error ? (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                ) : (
                    <>
                <div className="operation">
                    <section id="route-section">
                        {routes.length === 0 ? (
                            <p className="text-muted">Aucune route</p>
                        ) : routes.map((route) => (
                            <div
                                className="card mb-2 route-card"
                                key={route.route.id}
                                onClick={() => handleRouteClick(route)}
                            >
                                <div className="card-body card-route">
                                    <div className="card-route-info">
                                            <p className="mb-0"><strong>{route.route.name}</strong></p>
                                            <p className="mb-0">{route.driver?.name ?? ""}</p>
                                    </div>
                                    <div className="card-route-progress">
                                        {(() => {
                                            const total = route.point_visits.length;
                                            const success = route.point_visits.filter(p => p.delivery_status === "completed" && p.success_status === "meets_standards").length;
                                            const warning = route.point_visits.filter(p => p.delivery_status === "completed" && p.success_status === "below_standards").length;
                                            const danger = route.point_visits.filter(p => p.delivery_status === "failed" || (p.delivery_status === "completed" && p.success_status === "no_tasks_completed")).length;
                                            const completed = success + warning + danger;
                                            return (
                                                <>
                                                    <div className="progress">
                                                        <div className={`progress-bar${route.route.delivery_status === "in_progress" ? " progress-bar-striped progress-bar-animated" : ""} bg-success`} role="progressbar" style={{ width: `${(success / total) * 100}%` }}>{success}</div>
                                                        <div className={`progress-bar${route.route.delivery_status === "in_progress" ? " progress-bar-striped progress-bar-animated" : ""} bg-warning`} role="progressbar" style={{ width: `${(warning / total) * 100}%` }}>{warning}</div>
                                                        <div className={`progress-bar${route.route.delivery_status === "in_progress" ? " progress-bar-striped progress-bar-animated" : ""} bg-danger`} role="progressbar" style={{ width: `${(danger / total) * 100}%` }}>{danger}</div>
                                                    </div>
                                                    <div className="card-route-labels">
                                                        <div>
                                                            <small>{completed}/{total} arrêts</small>
                                                        </div>
                                                        <div className="card-route-shipments small">
                                                            <span>{route.shipments.dropoff_completed_count}/{route.shipments.dropoff_total_count} dépôts</span>
                                                            <span>{route.shipments.pickup_completed_count}/{route.shipments.pickup_total_count} collectes</span>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </section>
                    <section id="map-section">
                        <APIProvider
                            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                            libraries={["geometry"]}
                        >
                            <Map
                                defaultCenter={defaultCenter}
                                defaultZoom={12}
                                mapId="a24a5e7a0ff8f2636489d9a2"
                                style={{ width: "100%", height: "600px" }}
                            >
                                <MapController driverLocations={driverLocations} />
                                {Object.entries(driverLocations).map(([routeId, loc]) => {
                                    const route = routes.find((r) => String(r.route.id) === String(routeId));
                                    return (
                                        <AdvancedMarker
                                            key={`driver-${routeId}`}
                                            position={{ lat: Number(loc.latitude), lng: Number(loc.longitude) }}
                                            title={route ? `${route.route.name}${route.driver ? ` - ${route.driver.name}` : ''}` : `Route ${routeId}`}
                                        >
                                            <div style={{
                                                background: "#f5f5f5",
                                                color: "black",
                                                borderRadius: "50%",
                                                border: "2px solid #3b71ca",
                                                width: 32,
                                                height: 32,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 9,
                                                fontWeight: "bold",
                                                whiteSpace: "nowrap",
                                            }}>
                                                {route ? route.route.name : routeId}
                                            </div>
                                        </AdvancedMarker>
                                    );
                                })}
                            </Map>
                        </APIProvider>
                    </section>
                    </div>
                    </>
                    )}
            </div>
        </div>
    );
}
